import React, { useCallback, useEffect, useState } from 'react';

import { Stack, Text, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCircleCheckFilled, IconCircleOff } from '@tabler/icons-react';
import PackageTypeLabel from 'components/PackageTypeLabel';
import PackageStatusBadge from 'components/status-badges/PackageStatusBadge';
import Table from 'components/Table';
import { useAuth } from 'context/auth-context';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useAthletePackages from 'hooks/athletePackage/useAthletePackages';
import { CellProps } from 'react-table';
import { AthletePackageList, AthleteRetrieve } from 'types';
import { formatAthletePackageValidityDisplay } from 'utils/athlete-package.helpers';
import { parseDateTimeToDate } from 'utils/date-functions';

import AthletePackageDetail from './AthletePackageDetail';
import { AthletePackageSearchFilters } from '../types';

const AthletePackagesTable = ({
  athlete,
  searchFilters,
  permission = true,
}: {
  athlete: AthleteRetrieve;
  searchFilters: AthletePackageSearchFilters;
  permission?: boolean;
}): JSX.Element => {
  const theme = useMantineTheme();
  const { formatCurrency, dateFormat } = useLocalization();
  const { session } = useAuth();

  const [athletePackageId, setAthletePackageId] = useState<string | undefined>();
  const [isViewFormOpened, { open: openViewForm, close: closeViewForm }] = useDisclosure(false);
  const [tableData, setTableData] = useState<AthletePackageList[]>([]);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useAthletePackages({
      athlete: athlete.athleteId,
      ...searchFilters,
    });

  const handlePackageClick = useCallback(
    (ap: string): void => {
      setAthletePackageId(ap);
      openViewForm();
    },
    [openViewForm],
  );

  const handleClose = (): void => {
    closeViewForm();
    setAthletePackageId(undefined);
  };

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  const columns = [
    {
      Header: 'Status',
      accessor: 'status' as const,
      Cell: ({ value, row }: CellProps<AthletePackageList>) => {
        const { child, status } = row.original;
        return (
          <Stack spacing={2}>
            <PackageStatusBadge
              status={value}
              isCancelEarly={
                row.original.cancellationDate !== null &&
                row.original.status !== 'CANCELLED' &&
                row.original.status !== 'ENDED'
              }
            />
            {status === 'ENDED' && child && child.isChange && (
              <Text size='xs' c='dimmed' data-cy='endedChangedToIndicator'>
                {`Changed to ${child.packageName}`}
              </Text>
            )}
          </Stack>
        );
      },
    },
    {
      Header: 'Package Name',
      accessor: 'package.name' as const,
      Cell: ({ value, row }: CellProps<AthletePackageList>) => {
        const { child, parent, startDate, status } = row.original;
        const tz = session?.gym?.timezone;
        const showChangingTo = child && child.isChange && (status === 'ACTIVE' || status === 'HOLD');
        const showCreatedFrom = parent && parent.isChange && status === 'UPCOMING';
        const showChangedFrom = parent && parent.isChange && status === 'ACTIVE';
        return (
          <Stack spacing={2}>
            <Text
              data-cy='checkAthletePackageName'
              weight='bold'
              w='fit-content'
              underline
              sx={{ cursor: 'pointer' }}
              onClick={() => handlePackageClick(row.original.athletePackageId)}
            >
              {value}
            </Text>
            {showChangingTo && (
              <Text size='xs' c='dimmed' data-cy='changingToIndicator'>
                {`↳ Changing to ${child.packageName} on ${dayjs(
                  parseDateTimeToDate(child.startDate, tz),
                ).format(dateFormat)}`}
              </Text>
            )}
            {showCreatedFrom && (
              <Text size='xs' c='dimmed' data-cy='createdFromChangeIndicator'>
                {`↳ Created from change of ${parent.packageName}`}
              </Text>
            )}
            {showChangedFrom && (
              <Text size='xs' c='dimmed' data-cy='changedFromIndicator'>
                {`↳ Changed from ${parent.packageName} on ${dayjs(parseDateTimeToDate(startDate, tz)).format(
                  dateFormat,
                )}`}
              </Text>
            )}
          </Stack>
        );
      },
    },
    {
      Header: 'Total Price',
      accessor: 'billingPlan.totalPrice' as const,
      Cell: ({ value }: CellProps<AthletePackageList>) => formatCurrency(value / 100),
    },
    {
      Header: 'Payment',
      accessor: 'billingPlan' as const,
      Cell: ({ value }: CellProps<AthletePackageList>) => {
        let text = '';
        if (value.numberOfInstallments === 1) {
          text = '1 installment';
        } else {
          text = `${value.numberOfInstallments} ${value.isEqualPayments ? 'equal' : 'unequal'} installments`;
        }
        return text;
      },
    },
    {
      Header: 'Type',
      Cell: ({ row }: CellProps<AthletePackageList>) => (
        <PackageTypeLabel type={row.original.package.type} short />
      ),
    },
    {
      Header: 'Validity',
      accessor: 'validity' as const,
      Cell: ({ row }: CellProps<AthletePackageList>) => (
        <div>
          {formatAthletePackageValidityDisplay(row.original, dateFormat, ' - ', session?.gym?.timezone)}
        </div>
      ),
    },
    {
      Header: 'Renew',
      accessor: 'autoRenew' as const,
      Cell: ({ value }: CellProps<AthletePackageList>) =>
        value ? (
          <IconCircleCheckFilled size={theme.fontSizes.xl} style={{ color: theme.colors.kiloBlue[6] }} />
        ) : (
          <IconCircleOff size={theme.fontSizes.md} color='gray' />
        ),
    },
    {
      Header: 'AutoPay',
      accessor: 'autoPay' as const,
      Cell: ({ value }: CellProps<AthletePackageList>) =>
        value ? (
          <IconCircleCheckFilled size={theme.fontSizes.xl} style={{ color: theme.colors.kiloBlue[6] }} />
        ) : (
          <IconCircleOff size={theme.fontSizes.md} color='gray' />
        ),
    },
    {
      Header: 'Next Invoice',
      accessor: 'nextInvoiceDueDate' as const,
      Cell: ({ value }: CellProps<AthletePackageList>) => (
        <div data-cy='nextInvoiceCheckAthletePackage'>
          {value ? dayjs(parseDateTimeToDate(value, session?.gym?.timezone)).format(dateFormat) : '-'}
        </div>
      ),
    },
  ];

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
      />
      <AthletePackageDetail
        athletePackageId={athletePackageId}
        setAthletePackageId={setAthletePackageId}
        opened={isViewFormOpened}
        closeHandler={handleClose}
        permission={permission}
      />
    </>
  );
};

export default AthletePackagesTable;
